import { Controller } from "@hotwired/stimulus"

// One styled confirmation modal for every [data-turbo-confirm] form on the page.
// Registered as Turbo's global confirm method, so any Turbo-driven button/form
// with data-turbo-confirm routes here instead of window.confirm(). The confirm
// button enters a Processing… state and stays until the form submission
// navigates away — giving feedback during the slow Stripe round-trip.
//
// For money-affecting billing forms (data-preview-kind), the dialog opens with
// the confirm button disabled and "Calculating…" while it fetches the EXACT
// Stripe proration from the server, then shows the precise charge. If the
// preview fails, it falls back to the static turbo_confirm copy.
export default class extends Controller {
  static targets = ["dialog", "message", "confirm"]

  connect() {
    this._resolve = null
    this._registerConfirm()

    // confirm() leaves the dialog open (for the Processing… spinner) while the
    // form submits, relying on the page render to remove it. That only works for
    // a full body replacement. This app renders <meta turbo-refresh-method=morph>,
    // so a form that redirects to the same URL (e.g. seat updates → usage_path)
    // MORPHS instead of replacing the body — and morph neither fires
    // turbo:before-cache nor tears the <dialog> down. Worse, morph patches
    // <dialog open> back to <dialog> by stripping the attribute, which does NOT
    // exit the top layer (only dialog.close() does), leaving the whole page inert.
    // Close the live dialog before every render (morph or replace) and before
    // caching so it always leaves the top layer.
    this._closeDialog = () => this.dialogTarget.close()
    document.addEventListener("turbo:before-render", this._closeDialog)
    document.addEventListener("turbo:before-cache", this._closeDialog)

    // Dismissing the dialog natively (Esc key, backdrop) fires "close" without
    // going through cancel(). Settle the pending promise so Turbo's confirm
    // doesn't hang and block the next submission.
    this._onClose = () => this._settle(false)
    this.dialogTarget.addEventListener("close", this._onClose)
  }

  // Register Turbo's confirm handler exactly ONCE for the app, and resolve the
  // CURRENTLY-connected controller on each call. Registering per-connect (the old
  // way) captured this instance; after Turbo navigations the global handler could
  // end up pointing at a detached controller whose dialog isn't in the live DOM,
  // so showModal() silently no-ops and every confirm button "stops working".
  _registerConfirm() {
    if (window.__confirmModalRegistered) return
    const app = this.application
    const handler = (message, formElement, submitter) => {
      const el = document.querySelector('[data-controller~="confirm-modal"]')
      const ctrl = el && app.getControllerForElementAndIdentifier(el, "confirm-modal")
      return ctrl
        ? ctrl.prompt(message, formElement, submitter)
        : Promise.resolve(window.confirm(message))
    }
    const turbo = window.Turbo
    if (turbo && turbo.config && turbo.config.forms) {
      turbo.config.forms.confirm = handler
    } else if (turbo && turbo.setConfirmMethod) {
      turbo.setConfirmMethod(handler)
    }
    window.__confirmModalRegistered = true
  }

  disconnect() {
    document.removeEventListener("turbo:before-render", this._closeDialog)
    document.removeEventListener("turbo:before-cache", this._closeDialog)
    this.dialogTarget.removeEventListener("close", this._onClose)
  }

  async prompt(message, formElement, submitter) {
    // button_to puts data-preview-kind on the <button> (submitter); form_with
    // puts it on the <form>. Check both. FormData always comes from the form,
    // which carries the hidden params + CSRF token.
    const previewKind = submitter?.dataset?.previewKind || formElement?.dataset?.previewKind

    if (previewKind) {
      this.messageTarget.textContent = "Calculating…"
      this.confirmTarget.disabled = true
    } else {
      this.messageTarget.textContent = message
      this.confirmTarget.disabled = false
    }
    this.confirmTarget.textContent = "Confirm"
    this.dialogTarget.showModal()
    const promise = new Promise((resolve) => (this._resolve = resolve))

    if (previewKind) {
      const preview = await this._preview(message, formElement, previewKind)
      // Esc or the backdrop can dismiss the dialog while the preview is still in
      // flight; don't write the charge into a dialog the user already dismissed.
      if (this.dialogTarget.open) {
        this.messageTarget.textContent = preview
        this.confirmTarget.disabled = false
      }
    }

    return promise
  }

  async _preview(message, formElement, previewKind) {
    try {
      const url = this.element.dataset.previewUrl
      const fd = new FormData(formElement)
      fd.append("kind", previewKind)
      const resp = await fetch(url, {
        method: "POST",
        body: fd,
        // The form's own token is scoped to the form's action, so the preview
        // endpoint needs the page-wide one.
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      })
      const data = await resp.json()
      return (data.ok && data.message) ? data.message : message
    } catch (e) {
      return message
    }
  }

  confirm() {
    this.confirmTarget.disabled = true
    this.confirmTarget.innerHTML =
      '<span class="inline-flex items-center justify-center gap-2">' +
      '<span class="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin"></span>Processing…</span>'
    this._settle(true)
    // Intentionally leave the dialog open; the form submit will navigate and replace the page.
  }

  cancel() {
    this.dialogTarget.close()
    this._settle(false)
  }

  _settle(value) {
    if (this._resolve) {
      this._resolve(value)
      this._resolve = null
    }
  }
}
